<script>
import Vue from "vue";

export default Vue.extend({
	inheritAttrs: false,
	props: {
		label: {
			type: String,
			default: "",
		},
		type: {
			type: String,
			default: "text",
		},
		name: {
			type: String,
			default: "",
		},
		value: {
			type: [String, Number, Boolean, Array],
			default: "",
		},
		theme: String,
	},
	data() {
		return {
			classList: null,
			valIsBoolean: false,
			valIsArray: false,
		};
	},
	mounted() {
		// Get list of classes applied to parent, since they aren"t included in $attrs
		this.classList = this.$el.classList;

		// Determine whether value is boolean/array to help handling checkboxes/radios
		if (typeof this.value === "boolean") {
			this.valIsBoolean = true;
		}
		if (Array.isArray(this.value)) {
			this.valIsArray = true;
		}
	},
	computed: {
		inputClassList() {
			if (!this.classList) {
				return {};
			}
			return {
				"dark-theme": this.theme === "dark-theme",
				"light-theme": this.theme === "light-theme",
				disabled: this.$attrs.disabled || this.classList.contains("disabled"),
				focus: this.classList.contains("focus"),
				hover: this.classList.contains("hover"),
			}
		},
		hasLabel() {
			return this.label || !!this.$slots.default;
		},
		labelFirst() {
			return ["radio", "checkbox"].indexOf(this.type) === -1;
		},
		checked() {
			if (["radio", "checkbox"].indexOf(this.type) === -1) {
				return null;
			}
			if (this.type === "radio") {
				return this.value === this.$attrs.value;
			}
			if (this.valIsBoolean) {
				return !!this.value;
			}
			if (this.valIsArray) {
				return this.value.indexOf(this.$attrs.value) !== -1;
			}
		},
		listeners() {
			return {
				...this.$listeners,
				input: (event) => {
					let inputValue = event.target.value;

					// Handle checkboxes
					if (this.type === "checkbox") {
						// Return checked status for boolean checkboxes
						if (this.valIsBoolean) {
							inputValue = event.target.checked;
						}
						if (this.valIsArray) {
							let arr = this.value;
							if (event.target.checked) {
								arr.push(this.$attrs.value);
							} else {
								arr = arr.filter(val => val !== this.$attrs.value);
							}
							inputValue = [...new Set(arr)]; // Remove duplicates
						}
					}

					// Handle radio buttons
					if (this.type === "radio") {
						inputValue = this.$attrs.value;
					}

					this.$emit("input", inputValue);
				},
			};
		},
	},
});
</script>

<template>
	<label class="input">
		<span v-if="(hasLabel && labelFirst)">
			{{ label }}
			<slot></slot>
		</span>
		<input
			v-if="(type !== 'textarea')"
			:class="inputClassList"
			:name="name"
			:type="type"
			:value="value"
			:checked="checked"
			v-bind="$attrs"
			v-on="listeners"
		>
		<textarea
			v-if="(type === 'textarea')"
			:class="inputClassList"
			:name="name"
			:value="value"
			v-bind="$attrs"
			v-on="listeners"
		></textarea>
		<span v-if="(hasLabel && !labelFirst)">
			{{ label }}
			<slot></slot>
		</span>
		<div class="help-text text-small">
			<slot name="error"></slot>
		</div>
	</label>
</template>

<style lang="less" scoped>
@import (reference) "../assets/variables.less";

.input {
	display: inline-block;
	width: 100%;
	text-align: left;
}

input,
textarea {
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	display: block;
	font-family: @font-family-IntCircular-Light;
	font-size: @font-size-14;
	font-weight: @weight-light;
	line-height: @font-size-18;
	padding: @gap-smallest @gap-small;
	border-radius: @border-radius;
	box-sizing: border-box;
	border: 1px solid @ui-light-6;
	outline: none;
	width: 100%;

	&::-webkit-search-cancel-button {
		appearance: none;
	}

	&.light-theme {
		color: @ui-dark-6;
		background-color: @ui-light-1;
		border-color: @ui-light-6;
		&:not([disabled]):not(:disabled):not(.disabled) {
			&:hover, &.hover {
				color: @ui-dark-1;
				background-color: @ui-light-3;
				border-color: @ui-light-7;
			}
			&:focus, &.focus {
				border-color: @primary-dark-4;
			}
		}
		&::placeholder {
			color: rgba(@ui-dark-6, 0.75);
		}
		&::-ms-clear {
			color: @ui-dark-6;
		}
	}
	&.dark-theme {
		color: @ui-light-6;
		background-color: @ui-dark-1;
		border-color: @ui-dark-6;
		&:not([disabled]):not(:disabled):not(.disabled) {
			&:hover, &.hover {
				color: @ui-light-1;
				background-color: @ui-dark-3;
				border-color: @ui-dark-7;
			}
			&:focus, &.focus {
				border-color: @primary-light-4;
			}
		}
		&::placeholder {
			color: rgba(@ui-light-6, 0.75);
		}
		&::-ms-clear {
			color: @ui-light-6;
		}
	}

	&.disabled,
	&:disabled,
	&[disabled] {
		background-image: none;
		opacity: 0.6;
	}
}

input[type="checkbox"],
input[type="radio"] {
	display: inline-block;
	margin: 0 @gap-smallest 0 0;
	padding: 6px;
	width: auto;

	&.disabled,
	&:disabled,
	&[disabled] {
		& + span {
			background-image: none;
			opacity: 0.6;
		}
	}

	&.light-theme {
		color: @ui-dark-1;
		border-color: @ui-light-7;
		&:checked {
			background-color: @primary-dark-4;
		}
		&:not([disabled]):not(:disabled):not(.disabled) {
			&:hover, &.hover {
				& + span {
					color: @ui-dark-1;
					border-color: @ui-light-7;
				}
				&:checked {
					background-color: @primary-dark-3;
				}
			}
			&:focus, &.focus {
				&:checked {
					border-color: @ui-dark-1;
				}
			}
		}
	}
	&.dark-theme {
		color: @ui-light-1;
		border-color: @ui-dark-7;
		&:checked {
			background-color: @primary-light-4;
		}
		&:not([disabled]):not(:disabled):not(.disabled) {
			&:hover, &.hover {
				& + span {
					color: @ui-light-1;
					border-color: @ui-dark-7;
				}
				&:checked {
					background-color: @primary-light-3;
				}
			}
			&:focus, &.focus {
				&:checked {
					border-color: @ui-light-1;
				}
			}
		}
	}
}

input[type="radio"] {
	border-radius: @gap-small;
}

input[type="checkbox"] {
	border-radius: 0;
	position: relative;

	&:checked:before {
		content: "\2713";
		position: absolute;
		height: 0;
		top: 0;
		left: 0;
		margin-top: -3px;
		font-weight: @weight-bold;
	}
}

// Microsoft Edge needs some extra styling
@supports (-ms-ime-align: auto) {
	input[type="checkbox"] {
		&:checked {
			width: 0;
			top: -6px;
			height: 0;
		}
	}
}

input[type="number"]::-webkit-inner-spin-button {
	-webkit-appearance: none;
	appearance: none;
}
input[type="number"] {
	-moz-appearance: textfield;
	appearance: textfield;
}

input[type="search"] {
	padding-left: 35px;
	background-size: 15px 16px;
	background-repeat: no-repeat;
	background-position: 10px center;
	background-image: url("/portal-icons/search.svg");
}
</style>
